/**
 * useCopilotChat — gerencia o fluxo completo de mensagem do copiloto (TASK-21, ST-02).
 *
 * Protocolo (design.md §20.15, Req 9):
 *  - Envia POST /v1/copilot/chat com { message, context: { tenantId, businessUnitId } }.
 *  - Se Content-Type = text/event-stream: consome SSE, despachando STREAM_TOKEN por token.
 *  - Caso contrário: fallback para leitura de corpo JSON único (sem polling).
 *  - 503 → DEGRADED (Req 9.4 — SEM retry automático, SEM mock de resposta).
 *  - 4xx/5xx → ERROR recuperável.
 *
 * Contexto de tenant/BU (Req 9.5, Req 9.6):
 *  - tenantId e businessUnitId obtidos de useActiveScope (ActiveScopeProvider).
 *  - Bearer token obtido de useAuth.
 *
 * State machine: delegada a copilotReducer.ts (design.md §20.15, TASK-21 ST-04).
 *
 * Referências: design.md §20.15, Req 9, Req 9.4, Req 9.5, Req 9.6, TASK-21.
 */

import { useReducer, useCallback, useRef, useEffect } from "react";
import { useAuth } from "../../app/AuthProvider.js";
import { useActiveScope } from "../../app/providers/ActiveScopeProvider.js";
import {
  copilotReducer,
  INITIAL_COPILOT_STATE,
  type CopilotState,
  type CopilotAction,
} from "./copilotReducer.js";
import { postCopilotChat } from "../../data/repos/copilotRepo.js";

// ---------------------------------------------------------------------------
// Interface pública do hook
// ---------------------------------------------------------------------------

export interface UseCopilotChatReturn {
  /** Estado atual da state machine. */
  readonly state: CopilotState;
  /** Dispatch direto (OPEN/CLOSE/RETRY exposto para componentes externos). */
  readonly dispatch: React.Dispatch<CopilotAction>;
  /**
   * Envia uma mensagem para o copiloto.
   * Se o texto estiver vazio ou a requisição já estiver em andamento, é ignorado.
   */
  sendMessage(text: string): void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook central do copiloto. Integra state machine, fetch SSE e contexto de tenant/BU.
 *
 * @returns `{ state, dispatch, sendMessage }`
 */
export function useCopilotChat(): UseCopilotChatReturn {
  const { user } = useAuth();
  const { tenantId, businessUnitId } = useActiveScope();
  const [state, dispatch] = useReducer(copilotReducer, INITIAL_COPILOT_STATE);

  // AbortController para cancelar requisição anterior em duplo-envio.
  const abortRef = useRef<AbortController | null>(null);

  /**
   * Flag de desmontagem: evita dispatch após unmount, prevenindo o warning
   * "Can't perform a React state update on an unmounted component" (Finding F1).
   */
  const unmountedRef = useRef(false);

  /**
   * Cleanup de desmontagem: aborta qualquer fetch/stream ativo (Finding F1).
   * Ao fechar o drawer (unmount), o abort sinaliza o ReadableStream e nenhum
   * dispatch adicional ocorre graças à checagem de unmountedRef/signal.aborted.
   */
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      abortRef.current?.abort();
    };
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      // Previne envio durante loading/responding.
      if (state.status === "loading" || state.status === "responding") return;

      // open → loading (adiciona mensagem do usuário ao histórico).
      dispatch({ type: "SEND", userMessage: trimmed });

      // Cancela requisição anterior, se houver.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // ---- Executa a requisição -----------------------------------------------
      let response: Response;
      try {
        response = await postCopilotChat(
          // Payload: message + context (Req 9.5, Req 9.6, design.md §20.15).
          { message: trimmed, context: { tenantId, businessUnitId } },
          user?.token ?? null,
          tenantId,
          businessUnitId,
          controller.signal,
        );
      } catch {
        // Erro de rede (ex.: offline, fetch lançou).
        if (!controller.signal.aborted && !unmountedRef.current) {
          dispatch({ type: "ERROR", errorMessage: "Erro de conexão" });
        }
        return;
      }

      // Guard: não despacha se desmontado ou abortado antes da resposta resolver.
      if (unmountedRef.current || controller.signal.aborted) return;

      // ---- 503 → degraded (Req 9.4: provedor IA indisponível) -----------------
      // SEM retry automático — saída apenas por ação explícita do usuário (RETRY).
      if (response.status === 503) {
        dispatch({ type: "DEGRADED" });
        return;
      }

      // ---- Outros erros HTTP → error recuperável -------------------------------
      if (!response.ok) {
        dispatch({ type: "ERROR", errorMessage: `Erro ${response.status}` });
        return;
      }

      const contentType = response.headers.get("content-type") ?? "";

      if (contentType.includes("text/event-stream")) {
        // ---- Caminho SSE: tokens incrementais (design.md §20.15) ---------------
        dispatch({ type: "STREAM_START" }); // loading → responding

        if (!response.body) {
          dispatch({ type: "DONE" });
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        /**
         * Processa uma linha SSE já com terminador removido.
         *
         * Robustez do parser (Findings F2, F4):
         *  - Aceita "data: token" E "data:token" (espaço após ':' é opcional — RFC SSE §7.3).
         *  - Remove '\r' residual (terminadores CRLF — Finding F4).
         *  - Reconhece "[DONE]" independente de CRLF.
         *  - Não faz trim do valor do token: espaços são significativos (ex.: " ajuda.").
         */
        function processLine(raw: string): void {
          // Strip \r de terminadores CRLF (Finding F4)
          const line = raw.endsWith("\r") ? raw.slice(0, -1) : raw;
          if (!line.startsWith("data:")) return;
          // Slice "data:" (5 chars) e remove no máximo 1 espaço (spec SSE — Finding F2)
          const rawValue = line.slice(5);
          const data = rawValue.startsWith(" ") ? rawValue.slice(1) : rawValue;
          // "[DONE]" e valor vazio sinalizam fim do stream
          if (data === "[DONE]" || data === "") return;
          if (!unmountedRef.current) {
            dispatch({ type: "STREAM_TOKEN", token: data });
          }
        }

        /**
         * Drena linhas completas do buffer acumulado e retorna o fragmento residual.
         * Linhas são separadas por '\n'; '\r\n' é tratado via strip de '\r' em processLine.
         */
        function drainBuffer(buf: string): string {
          const lines = buf.split("\n");
          // A última "linha" pode estar incompleta — devolve como novo buffer.
          const remainder = lines.pop() ?? "";
          for (const line of lines) {
            processLine(line);
          }
          return remainder;
        }

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              /**
               * Flush do buffer residual (Finding F3):
               * O último evento pode terminar sem '\n\n' final. Processa qualquer
               * conteúdo remanescente no buffer antes do DONE.
               */
              if (buffer) processLine(buffer);
              break;
            }
            if (unmountedRef.current || controller.signal.aborted) break;

            buffer += decoder.decode(value, { stream: true });
            buffer = drainBuffer(buffer);
          }
          if (!unmountedRef.current && !controller.signal.aborted) {
            dispatch({ type: "DONE" }); // responding → open
          }
        } catch {
          if (!controller.signal.aborted && !unmountedRef.current) {
            dispatch({ type: "ERROR", errorMessage: "Erro durante leitura do stream" });
          }
        } finally {
          reader.releaseLock();
        }
      } else {
        // ---- Fallback JSON único (design.md §20.15 — sem SSE no gateway) --------
        dispatch({ type: "STREAM_START" }); // loading → responding
        try {
          const body = (await response.json()) as {
            content?: string;
            message?: string;
            text?: string;
          };
          const content = body.content ?? body.message ?? body.text ?? "";
          if (content && !unmountedRef.current) {
            dispatch({ type: "STREAM_TOKEN", token: content });
          }
          if (!unmountedRef.current) {
            dispatch({ type: "DONE" }); // responding → open
          }
        } catch {
          if (!unmountedRef.current) {
            dispatch({ type: "ERROR", errorMessage: "Erro ao interpretar resposta" });
          }
        }
      }
    },
    // Estado necessário para o guard de loading/responding.
    // tenantId/businessUnitId/token usados no payload.
    [state.status, user?.token, tenantId, businessUnitId],
  );

  return { state, dispatch, sendMessage };
}
